import React, { FunctionComponent, ReactNode } from 'react'
import clsx from 'clsx'

import { makeStyles } from '@material-ui/core/styles'
import { AppBar, Tab, Tabs } from '@material-ui/core'
import { AppBarProps } from '@material-ui/core/AppBar/AppBar'

import { useAutoTabsStyles } from './AutoTabs'
import { tabA11yProps, TabPanel } from './TabPanel'

import { required } from '../common/objectUtils'

export type ReactiveTab<T> = {
  value: T

  title: ReactNode
}

export type ReactiveTabsProps<T> = {
  /**
   * Value of the active tab, one of the {@link tabs}.
   */
  value: T

  tabs: ReactiveTab<T>[]

  onChange: (newTabValue: T) => void

  secondary?: boolean

  appBarProps?: Omit<AppBarProps, 'position' | 'className'>

  children: ReactNode

  AfterTabsComponent?: FunctionComponent
}

const tabIndicatorProps = {hidden: true}

/**
 * Renders the AppBar like AutoTabs, but has a single child in the TabPanel.
 */
export const ReactiveTabs =
  <T extends unknown>(
    {
      value, tabs, onChange,
      secondary, appBarProps,
      children, AfterTabsComponent
    }: ReactiveTabsProps<T>) => {
    const classes = useStyles()
    const autoTabClasses = useAutoTabsStyles()

    const onTabsChange =
      (event: unknown, newTabValue: unknown) => {
        // Re-validate the new value vs props - comes in as `any` from MUI
        const newTab = required(tabs.find(_ => _.value === newTabValue))
        onChange(newTab.value)
      }

    return <>
      <AppBar
        position="relative"
        className={clsx(autoTabClasses.appBar, autoTabClasses.appBarPrimary,
          secondary && autoTabClasses.appBarSecondary, 'reactive_tabs', classes.appBar)}
        {...appBarProps}
      >
        <Tabs
          variant="scrollable"
          className={classes.tabs}
          value={value}
          onChange={onTabsChange}
          TabIndicatorProps={tabIndicatorProps}
        >
          {
            // Children must have the `value` prop for onChange to work properly
            tabs.map(
              (tab, index) =>
                <Tab
                  key={index}
                  disableRipple
                  value={tab.value}
                  label={
                    typeof tab.title === 'string'
                    ? <span>{tab.title}</span>
                    : tab.title
                  } {...tabA11yProps(index)}
                />)
          }
        </Tabs>

        {
          AfterTabsComponent !== undefined &&
          <AfterTabsComponent/>
        }
      </AppBar>

      {
        <TabPanel value={0} index={0}>
          {children}
        </TabPanel>
      }
    </>
  }

const useStyles = makeStyles(
  (theme) => ({
    appBar: {
      maxHeight: '50px',
      marginTop: '20px'
    },
    tabs: {
      maxWidth: '100%',
      minWidth: 0,
      '& button': {},
      '& p': {
        fontSize: 12,
        fontWeight: 400,
        color: theme.palette.mpSkladPrimary.gray
      },
      '& .Mui-selected .MuiTab-wrapper span:first-child': {
        color: theme.palette.mpSkladPrimary.blue,
        fontSize: 12,
        fontWeight: 600,
        borderBottom: '2px solid',
        display: 'flex',
        justifyContent: 'flex-start',
        alignItems: 'center',
        textAlign: 'left',
        width: 'fit-content'
      },
      '& .MuiTab-wrapper span:first-child': {
        display: 'flex',
        justifyContent: 'flex-start',
        alignItems: 'center',
        textAlign: 'left',
        width: 'fit-content'
      },
      '& .MuiTab-root': {
        padding: 0,
        minWidth: 'fit-content',
        marginRight: 40,
        overflow: 'unset'
      },
      '& .MuiTab-wrapper': {
        display: 'block'
      }
    }
  })
)